<script setup lang="ts">
/**
 * The entities in the scope of consolidation.
 *
 * A card per entity rather than a grid, because five narrow columns in a rail
 * this width would leave every field too small to read a company name in. The
 * page still shows them as the table they are.
 *
 * The method is chosen rather than typed: there are three, they are the three
 * the standard names, and a scope where one entity says "Full" and another
 * says "Intégration globale" is a scope somebody has to normalise by hand.
 */
import { useI18n } from 'vue-i18n'

import InspectorField from '@/sections/InspectorField.vue'
import { newScopeEntry, type ScopeEntry, type ScopeSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: ScopeSection }>()
const emit = defineEmits<{ change: [section: ScopeSection] }>()

/** The three the standard names. */
const METHODS = ['full', 'proportionate', 'equity'] as const

function set(changes: Partial<ScopeSection>): void {
  emit('change', { ...props.section, ...changes })
}

function setEntry(id: string, changes: Partial<ScopeEntry>): void {
  set({
    entries: props.section.entries.map((one) => (one.id === id ? { ...one, ...changes } : one)),
  })
}

function add(): void {
  set({ entries: [...props.section.entries, newScopeEntry()] })
}

function remove(id: string): void {
  set({ entries: props.section.entries.filter((one) => one.id !== id) })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.scope.title')">
      <input
        class="b-input"
        :value="section.title"
        @input="set({ title: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <div class="entries">
      <article v-for="entry in section.entries" :key="entry.id" class="entry">
        <header class="entry__head">
          <input
            class="b-input entry__name"
            :value="entry.entity"
            :placeholder="t('inspector.scope.entityPlaceholder')"
            @input="setEntry(entry.id, { entity: ($event.target as HTMLInputElement).value })"
          />
          <button
            type="button"
            class="b-icon-btn"
            :aria-label="t('inspector.scope.removeEntry')"
            @click="remove(entry.id)"
          >
            &times;
          </button>
        </header>

        <div class="entry__row">
          <input
            class="b-input"
            :value="entry.country"
            :placeholder="t('inspector.scope.countryPlaceholder')"
            @input="setEntry(entry.id, { country: ($event.target as HTMLInputElement).value })"
          />
          <select
            class="b-select"
            :value="entry.method"
            :aria-label="t('sectionFields.scope.method')"
            @change="setEntry(entry.id, { method: ($event.target as HTMLSelectElement).value })"
          >
            <option value="">{{ t('inspector.scope.methodPlaceholder') }}</option>
            <option v-for="method in METHODS" :key="method" :value="t(`methods.${method}`)">
              {{ t(`methods.${method}`) }}
            </option>
          </select>
        </div>

        <div class="entry__row">
          <input
            class="b-input entry__percent"
            :value="entry.controlPct"
            :placeholder="t('sectionFields.scope.control')"
            @input="setEntry(entry.id, { controlPct: ($event.target as HTMLInputElement).value })"
          />
          <input
            class="b-input entry__percent"
            :value="entry.interestPct"
            :placeholder="t('sectionFields.scope.interest')"
            @input="setEntry(entry.id, { interestPct: ($event.target as HTMLInputElement).value })"
          />
        </div>
      </article>
    </div>

    <button type="button" class="b-btn b-btn--outline" @click="add">
      {{ t('inspector.scope.addEntry') }}
    </button>

    <InspectorField :label="t('inspector.scope.caption')">
      <input
        class="b-input"
        :value="section.caption"
        @input="set({ caption: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.entries {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.entry {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-5);
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.entry__head {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
}

.entry__name {
  font-weight: 500;
}

.entry__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}

.entry__percent {
  font-variant-numeric: tabular-nums;
  text-align: right;
}
</style>
